import { LitElement, html, css } from 'lit'
import { focusTrap } from '../../lib/focus-trap.js'

export class SideDrawer extends LitElement {
  static styles = [
    css`
      .sidebar {
        width: 100%;
        position: fixed;
        z-index: 2147483002;
        bottom: 0;
        transition: transform 0.35s ease-in-out;
        overflow-y: hidden;
      }

      .sidebar.right {
        right: 0;
        transform: translateX(105%);
      }

      .sidebar.left {
        left: 0;
        transform: translateX(-105%);
      }

      .sidebar[aria-expanded='true'] {
        transform: none;
      }

      header {
        display: flex;
        align-items: center;
        margin: 0 auto;
        justify-content: center;
      }
    `,
  ]
  /**
   * @description Describes attributes on the element that act as inputs on this component
   * @prop {'left' | 'right'} [position='left'] Positions the side drawer to open on the left or right of the viewport
   * @prop {boolean} [open=false] - Open state of side drawer
   * @prop {string} id - The id of the side drawer used by side-drawer-btn to toggle specific side drawer
   * @prop {string} [top='0px'] - ex. '100px' Moves the top of the side drawer down a certain amount of pixels
   * @prop {string} [topElement] - An element selector  that moves the top of the side drawer down by the height of that element
   * @prop {string} [background='#fff'] - Background style of the side drawer
   * @prop {integer}[mediaMax=null] - Correlates to media-max attribute which hides side drawer above a certain device width. i.e. if (width > to 767) then hide.
   */
  static properties = {
    position: { type: String },
    open: { type: Boolean, reflect: true },
    id: { type: String, reflect: true },
    top: { type: String },
    topElement: { type: String, attribute: 'top-element' },
    background: { type: String },
    label: { type: String },
    mediaMax: { type: Number, attribute: 'media-max' },
    maxWidth: { type: String, attribute: 'max-width' },
    withHeader: { type: Boolean, attribute: 'with-header' },
  }

  connectedCallback() {
    super.connectedCallback()
    this.position = this.position ?? 'left'
    this.open = this.open ?? false
    this.top = this.top ?? '0px'
    this.background = this.background ?? '#fff'
    this.mediaMax = this.mediaMax ?? null
    this.maxWidth = this.maxWidth ?? '430px'
    this.throttled = false
    this.withHeader = this.withHeader ?? false
    this.title = this.title ?? ''
    this.label = this.label ?? 'Side Drawer'
    this.resizeHandler = () => {
      if (!this.throttled) {
        this._setTop()
        this.throttled = true
        setTimeout(() => {
          this.throttled = false
        }, 50)

        //close sidebar at certain device size
        if (this.mediaMax && window.innerWidth > this.mediaMax) {
          if (this.open) window.MicroBus.emit(`${this.id}:toggle`)
        }
      }
    }

    this.focusTrap = new focusTrap({
      el: this,
      deep: true,
      escCallback: () => {
        this._toggle()
      },
    })

    window.MicroBus.on(`${this.id}:toggle`, (e) => {
      this._setTop()
      this._toggle()

      let count = 0
      const reduceAttentiveZIndex = () => {
        if (document.getElementById('attentive_overlay')) {
          document.getElementById('attentive_overlay').setAttribute('style', 'z-index: 999999 !important')
        } else if (count < 10) {
          count++
          setTimeout(reduceAttentiveZIndex, 1000)
        }
      }

      reduceAttentiveZIndex()
    })

    window.MicroBus.on(`${this.id}:updated`, (e) => {
      this.focusTrap.updateElements()
    })
  }

  disconnectedCallback() {
    super.connectedCallback()
    window.MicroBus.off(`${this.id}:toggle`)
  }

  updated() {
    //start resize listener when side drawer is open and remove when closed to be more performant
    if (this.open) {
      window.addEventListener('resize', this.resizeHandler)
    } else {
      window.removeEventListener('resize', this.resizeHandler)
    }
  }

  render() {
    return html`
      <div
        class="sidebar ${this.position}"
        aria-expanded=${this.open}
        role="dialog"
        aria-model="true"
        aria-label="${this.label}"
        style="height: calc(100% - ${this.top}); background: ${this.background}; max-width: ${this.maxWidth};"
      >
        <slot name="close" @click=${this._close}></slot>
        <slot></slot>
      </div>
      <body-overlay top=${this.top} id="${this.id}-overlay"></body-overlay>
    `
  }

  _close() {
    window.MicroBus.emit(`${this.id}:toggle`)
  }

  _toggle() {
    this.open = !this.open
    if (this.open) {
      this.removeAttribute('hidden')
      this.focusTrap.activate()
    } else {
      this.focusTrap.deactivate()
      setTimeout(() => {
        this.setAttribute('hidden', 'true')
      }, 350)
    }
    window.MicroBus.emit(`${this.id}-overlay:toggle`, { closeEvent: `${this.id}:toggle` })
  }

  _setTop() {
    if (this.topElement) {
      const el = document.querySelector(this.topElement)
      const height = el.offsetHeight
      this.top = height + 'px'
    }
  }
}

customElements.define('side-drawer', SideDrawer)
